import {
  DefaultEntityPresentationApi,
  type DefaultEntityPresentationApiRenderer,
} from '@backstage/plugin-catalog';
import {
  defaultEntityPresentation,
  type EntityRefPresentationSnapshot,
} from '@backstage/plugin-catalog-react';
import type { CatalogApi } from '@backstage/catalog-client';
import { getResourceIcon } from './resourceIcons';

/**
 * Relabels catalog kinds to the infra terminology in the UI, everywhere the
 * entity presentation API is used (entity ref links, autocomplete, cards, …):
 *
 *   Component -> Application
 *   System    -> Repository
 *
 * Backstage renders the kind prefix of an entity ref (e.g. `system:foo`) via
 * this API, so overriding the renderer is the single place to change how those
 * refs read. The sidebar kind picker labels are driven separately and are not
 * covered here.
 *
 * See docs/catalog-entity-model.md for the model these labels come from.
 */

/** Lower-cased kind -> display label. Only kinds we rename need an entry. */
const KIND_LABELS: Record<string, string> = {
  component: 'Application',
  system: 'Repository',
};

/** Returns the relabelled kind, or the original if we don't rename it. */
export function relabelKind(kind: string): string {
  return KIND_LABELS[kind.toLocaleLowerCase('en-US')] ?? kind;
}

/**
 * Rewrites a short entity ref like `system:default/foo` or `component:foo`
 * so the kind prefix uses the relabelled, human-friendly kind
 * (`repository:foo`). Titles without a kind prefix are returned unchanged.
 */
function relabelRef(text: string): string {
  // Match a leading `kind:` prefix (kinds are single tokens, no slash/space).
  return text.replace(/^([a-z0-9-]+):/i, (whole, kind: string) => {
    const label = KIND_LABELS[kind.toLocaleLowerCase('en-US')];
    return label ? `${label.toLocaleLowerCase('en-US')}:` : whole;
  });
}

const renderer: DefaultEntityPresentationApiRenderer = {
  async: true,
  render: ({ entityRef, loading, entity, context }) => {
    // Start from Backstage's default snapshot, then relabel the kind tokens.
    const base = defaultEntityPresentation(entity ?? entityRef, context);
    const icon = getResourceIcon(entity);
    const snapshot: Omit<EntityRefPresentationSnapshot, 'entityRef'> = {
      ...base,
      primaryTitle: relabelRef(base.primaryTitle),
      ...(base.secondaryTitle
        ? { secondaryTitle: relabelRef(base.secondaryTitle) }
        : {}),
      ...(icon ? { Icon: icon } : {}),
    };
    // `loading` is part of the renderer contract; nothing extra to do here.
    void loading;
    return { snapshot };
  },
};

/** Builds the relabelling entity presentation API bound to the catalog API. */
export function createEntityPresentationApi(catalogApi: CatalogApi) {
  return DefaultEntityPresentationApi.create({ catalogApi, renderer });
}
